import { forwardRef, useEffect, useLayoutEffect, useState } from 'react'
import Styles from './EditableModalTitle.module.scss'

import { EditableModalTitleProps } from './Types'
import { useButton } from '@react-aria/button'
import { useObjectRef } from '@react-aria/utils'
import { useKeyboard } from '@react-aria/interactions'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Input } from 'almanac-ui/TextInputField/Input'
import { ArrowRight, Edit } from 'almanac-ui/icons/16'
import { IconButton } from 'components/IconButton/IconButton'
import cx from 'classnames'

export const Title = forwardRef<HTMLHeadingElement, { title: string; onPress?: () => void; onFocus?: () => void }>(
  (props, fRef) => {
    const { title, onPress, onFocus } = props
    const ref = useObjectRef(fRef)
    const { isMobile } = useResponsiveDesign()
    const { buttonProps } = useButton({ elementType: 'div', onPress }, ref)

    return (
      <div
        className={cx(Styles.titleContainer, { [Styles.isMobile]: isMobile, [Styles.pressable]: !!onPress })}
        ref={ref}
        {...buttonProps}
        onFocus={onFocus}
      >
        <span className={Styles.title}>{title}</span>
      </div>
    )
  },
)

const EditableModalTitle = forwardRef<HTMLTextAreaElement, EditableModalTitleProps>(
  ({ value, onChange, placeholder }, fRef) => {
    const { isMobile } = useResponsiveDesign()
    const [textarea, setTextarea] = useState<HTMLTextAreaElement | null>(null)
    const [showInput, setShowInput] = useState(!isMobile)

    const { keyboardProps } = useKeyboard({
      onKeyDown: (e) => {
        if (e.key === 'Escape' || e.key === 'Enter') {
          e.currentTarget.blur()
        }
      },
    })

    const selectAll = (input: HTMLTextAreaElement) => {
      input.setSelectionRange(input.value.length, input.value.length)
    }

    useLayoutEffect(() => {
      setShowInput(!isMobile)
    }, [isMobile])

    // Autoresize
    useEffect(() => {
      if (!textarea) {
        return
      }
      textarea.style.height = '0px'
      textarea.style.height = `${textarea.scrollHeight}px`
    }, [value, textarea])

    if (isMobile) {
      if (showInput) {
        return (
          <div className={Styles.inputContainer}>
            <Input
              value={value}
              autoFocus
              placeholder={placeholder}
              onChange={(e) => onChange(e.currentTarget.value)}
              size="lg"
              name="title"
              onBlur={() => {
                setShowInput(false)
              }}
              className={Styles.mobileInput}
              {...keyboardProps}
            />
            <IconButton icon={ArrowRight} size="lg" onPress={() => setShowInput(false)} variant="primary" />
          </div>
        )
      } else {
        return (
          <div className={Styles.mobileTitleContainer}>
            <Title
              title={value || placeholder || 'Untitled'}
              onPress={() => setShowInput(true)}
              onFocus={() => setShowInput(true)}
            />
            <IconButton icon={Edit} size="lg" onPress={() => setShowInput(true)} />
          </div>
        )
      }
    }

    if (showInput) {
      return (
        <textarea
          className={Styles.textarea}
          ref={(el) => {
            if (el) {
              if (fRef) {
                if (typeof fRef === 'function') {
                  fRef(el)
                } else {
                  fRef.current = el
                }
              }
              setTextarea(el)
            }
          }}
          autoFocus
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.currentTarget.value)}
          onFocus={(event) => {
            selectAll(event.target)
          }}
          onBlur={() => {
            if (!value) {
              return
            }
            setShowInput(false)
          }}
          {...keyboardProps}
        />
      )
    }

    return <Title title={value} onPress={() => setShowInput(true)} onFocus={() => setShowInput(true)} />
  },
)

export { EditableModalTitle }
